import { Translate, useTranslate } from '~/i18n';
import { LineArrowRight } from '~/svgs/common';
import { SearchIcon } from '~/svgs/navbar';
import Button from '../common/button';
import { DatesFilter } from './dates-filters';
import Input from '~/components/common/input';
import cn from 'classnames';
import { useRouter } from 'next/router';
import React, { useRef } from 'react';
import useMedia from '~/hooks/useMedia';
import { useState } from 'react';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { debounce } from 'lodash';
import {
   expFiltersDateSelectorEvent,
   expFiltersSearchBtnEvent,
} from '~/utils/mixpanel-events/experience-filters';
import { isJarir } from '~/utils/theme';

type SearchProps = {
   query?: string;
   dates?: {
      start: Date;
      end: Date;
   } | null;
};
export const FiltersSearchForm = () => {
   const router = useRouter();
   const { dir, translate } = useTranslate();
   const isTablet = useMedia(1100);
   const inputRef = useRef<HTMLInputElement>(null);
   const buttonRef = useRef<HTMLButtonElement>(null);
   const [search, setSearch] = useState<SearchProps>({});

   useEffect(() => {
      const { start_date, end_date } = router.query;
      if (start_date && end_date) {
         setSearch(prev => ({
            ...prev,
            dates: {
               start: new Date(String(start_date)),
               end: new Date(String(end_date)),
            },
         }));
      }
   }, [router]);

   const inputKeydown = (e: React.KeyboardEvent) => {
      const dateRef = document.getElementById('datepicker-btn') as HTMLButtonElement;

      switch (e.code) {
         case 'ArrowRight':
            dir === 'ltr' && dateRef?.focus?.();
            break;
         case 'ArrowLeft':
            dir === 'rtl' && dateRef?.focus?.();
            break;
         default:
            return;
      }
   };
   const dateKeydown = (e: React.KeyboardEvent) => {
      switch (e.code) {
         case 'ArrowRight':
            dir === 'ltr' ? buttonRef.current?.focus?.() : inputRef.current?.focus?.();
            break;
         case 'ArrowLeft':
            dir === 'ltr' ? inputRef.current?.focus?.() : buttonRef.current?.focus?.();
            break;
         default:
            return;
      }
   };
   const buttonKeydown = (e: React.KeyboardEvent) => {
      const dateRef = document.getElementById('datepicker-btn') as HTMLButtonElement;

      switch (e.code) {
         case 'ArrowRight':
            dir !== 'ltr' && dateRef?.focus?.();
            break;
         case 'ArrowLeft':
            dir !== 'rtl' && dateRef?.focus?.();
            break;
         default:
            return;
      }
   };

   const datesChangedHandler = (dates: { start: Date; end: Date } | null) => {
      dates &&
         expFiltersDateSelectorEvent(
            DateTime.fromJSDate(dates?.start).toFormat('yyyy-MM-dd'),
            DateTime.fromJSDate(dates?.end).toFormat('yyyy-MM-dd')
         );
      if (!isTablet) {
         setSearch(prev => ({ ...prev, dates }));
         return;
      }
      const obj = { ...router.query };
      // Reset page filter
      if (obj?.page) delete obj.page;
      if (dates?.start && dates?.end) {
         router.push({
            query: {
               ...obj,
               start_date: DateTime.fromJSDate(dates.start).toFormat('yyyy-MM-dd'),
               end_date: DateTime.fromJSDate(dates.end).toFormat('yyyy-MM-dd'),
            },
         });
      } else {
         delete obj.start_date;
         delete obj.end_date;
         router.push({
            query: {
               ...obj,
            },
         });
      }
   };
   const searchByQuery = debounce((query: string) => {
      const obj = { ...router.query };
      // Reset page filter
      if (obj?.page) delete obj.page;
      if (query) obj.query = query;
      else delete obj.query;
      router.push({
         query: {
            ...obj,
         },
      });
   }, 500);

   const queryChangedHandler = (query: string) => {
      if (!isTablet) {
         setSearch(prev => ({ ...prev, query }));
         return;
      }
      searchByQuery(query);
   };

   const submitForm = (e: React.FormEvent) => {
      e.preventDefault();

      const obj: Record<string, any> = { ...router.query };
      // Reset page filter
      if (obj?.page) delete obj.page;
      if ((search.query || search.dates) && (obj.query || (obj.start_date && obj.end_date)))
         expFiltersSearchBtnEvent(
            search.query ? search.query : obj.query,
            search.dates
               ? DateTime.fromJSDate(search.dates?.start).toFormat('yyyy-MM-dd')
               : obj.start_date,
            search.dates
               ? DateTime.fromJSDate(search.dates?.end).toFormat('yyyy-MM-dd')
               : obj.end_date
         );
      if (search.dates) {
         obj.start_date = DateTime.fromJSDate(search.dates.start).toFormat('yyyy-MM-dd');
         obj.end_date = DateTime.fromJSDate(search.dates.end).toFormat('yyyy-MM-dd');
      } else {
         delete obj.start_date;
         delete obj.end_date;
      }
      if (search.query) obj.query = search.query;
      else delete obj.query;
      router.push({
         query: {
            ...obj,
         },
      });
   };

   return (
      <form
         onSubmit={submitForm}
         className="absolute bottom-0 left-0 flex justify-center w-full tablet:w-auto tablet:relative">
         <Input
            id="filters-search"
            groupClassName="transform translate-y-1/2 z-40 tablet:translate-x-0 tablet:translate-y-0 shadow-lg rounded-full"
            innerRef={inputRef}
            onKeyDown={inputKeydown}
            defaultValue={router.query.query || ''}
            onChange={e => queryChangedHandler(e.currentTarget.value)}
            prependClassName="ltr:!rounded-l-full bg-white rtl:!rounded-r-full !px-0 !border-0"
            appendClassName="rtl:!rounded-l-full bg-white ltr:!rounded-r-full !px-0 !border-0"
            className="!h-14 sm:w-[300px] max-w-full focus:ring-2 border-r focus:ring-primary-color focus:z-10 -mx-1 !border-0"
            placeholder={translate({
               id: isJarir ? 'jarir:search placeholder' : 'filters:search halayalla',
            })}
            prepend={
               <label htmlFor="filters-search" aria-label="search" className="px-4">
                  <SearchIcon />
               </label>
            }
            append={
               <div className="tablet:flex items-center h-full tablet:px-1.5">
                  <DatesFilter dateKeydown={dateKeydown} onApply={datesChangedHandler} />
                  <div className="ltr:pl-2 rtl:pr-2">
                     <Button
                        onKeyDown={buttonKeydown}
                        ref={buttonRef}
                        btnType="submit"
                        className="!py-2 items-center !rounded-full tablet:flex hidden">
                        <Translate id="common:go" />
                        <LineArrowRight
                           className={cn(
                              'transform scale-75 ltr:ml-2 rtl:mr-2',
                              dir === 'rtl' && 'rotate-180'
                           )}
                        />
                     </Button>
                  </div>
               </div>
            }
         />
      </form>
   );
};
